import React, { useState, memo, useEffect } from 'react';
import Popups from '../../components/Popup';
import { usePopupContext } from '../../contexts/PopupContext';

const MailingInfoSendPopupComponent = ({ popup }) => {
  const { openPopup } = usePopupContext();
  const [isSubmitted, setSubmitted] = useState(false);
  const [uploadedFilesCount, setUploadedFilesCount] = useState(0);

  // ⚙️ New nested structure: [{ targetId, files: [File, ...] }, ...]
  const groupedFiles = popup.getIn(['props', 'files']).toJS();

  const queryResults = popup.getIn(['props', 'mailingInfo', 'queryResults']);
  const onOk = popup.getIn(['props', 'onOk']);
  const onCancel = popup.getIn(['props', 'onCancel']);
  const uploadEmailAttachment = popup.getIn(['props', 'uploadEmailAttachment']);

  // Flatten all files into a single array with targetId references
  const allFiles = groupedFiles.flatMap(group =>
    group.files.map(file => ({
      file,
      targetId: group.targetId,
    })),
  );

  const message = `You are about to send an email for all targets main contacts (${queryResults.size} total)`;

  useEffect(() => {
    // When confirmed — start sequential uploads
    if (isSubmitted && allFiles.length > 0) {
      const uploaded = [];
      const failed = [];

      uploadNextFile(0, uploaded, failed);
    }

    // If no files — finish immediately
    if (isSubmitted && allFiles.length === 0) {
      onOk();
    }
  }, [isSubmitted]);

  const handleClose = () => {
    onCancel();
  };

  const handleConfirm = () => {
    setSubmitted(true);
  };

  /**
   * Uploads files sequentially, one by one.
   */
  const uploadNextFile = (index, uploaded, failed) => {
    // ✅ All files processed
    if (index >= allFiles.length) {
      if (failed.length > 0) {
        openPopup('ErrorPopup', {
          error: `${failed.map(f => f.error).join('\n')}`,
        });

        return;
      }

      // Group uploaded results by targetId before finishing
      const groupedUploaded = Object.values(
        uploaded.reduce((accumulator, { targetId, attachmentId }) => {
          if (!accumulator[targetId]) {
            accumulator[targetId] = { targetId, attachmentIds: [] };
          }

          accumulator[targetId].attachmentIds.push(attachmentId);

          return accumulator;
        }, {}),
      );

      // Pass successfully uploaded attachments to onOk
      onOk(groupedUploaded);

      return;
    }

    const { file, targetId } = allFiles[index];

    // 🔍 Find matching query by targetId
    const query = queryResults.find(q => q.targetId === targetId);

    // If no query found for this target — skip file but continue
    if (!query) {
      failed.push({
        file,
        error: `No query data found for targetId ${targetId}`,
      });
      setUploadedFilesCount(index + 1);
      uploadNextFile(index + 1, uploaded, failed);

      return;
    }

    const formData = new FormData();

    formData.append('file', file);
    formData.append('target_id', targetId);
    formData.append('batch_id', query.batchId);

    uploadEmailAttachment({
      formData,
      afterSuccess: ({ response }) => {
        if (response.status === 'success') {
          // ✅ Store successfully uploaded file info
          uploaded.push({
            attachmentId: response.data.attachmentId,
            targetId,
          });
        } else {
          failed.push({
            file,
            error: `Upload failed "${file.name}": ${response.statusText || 'Unknown server response'}`,
          });
        }

        setUploadedFilesCount(index + 1);
        uploadNextFile(index + 1, uploaded, failed);
      },
      afterError: ({ response, responseBody }) => {
        let errorMessage;

        if (response.status === 413) {
          errorMessage = `Upload failed "${file.name}": Content too large.`;
        } else if (!response.body) {
          errorMessage = `Upload failed "${file.name}": ${response.statusText || 'Unknown error'}`;
        } else {
          errorMessage = `Upload failed "${file.name}": ${responseBody?.message || 'Unexpected error'}`;
        }

        failed.push({ file, error: errorMessage });
        setUploadedFilesCount(index + 1);
        uploadNextFile(index + 1, uploaded, failed);
      },
    });
  };

  return (
    <Popups.MailingInfoSendPopup
      filesCount={allFiles.length}
      isSubmitted={isSubmitted}
      message={message}
      onClose={handleClose}
      onConfirm={handleConfirm}
      uploadedFilesCount={uploadedFilesCount}
    />
  );
};

export const MailingInfoSendPopup = memo(MailingInfoSendPopupComponent);
